import { useEffect } from 'react'
import { analytics } from '@omnea-app/fe-analytics/client'
import { FeatureFlagsContextValue } from './types'

/**
 * Feature flags to track as user-level properties in Mixpanel.
 * If we were on the higher Mixpanel plan, we could track this at the group/organisation level,
 * but we're not, so we track it at the user level.
 */
const ANALYTICS_TRACKED_FLAGS = [
  'FORM_LEVEL_AI_DOC_EXTRACTION',
  'ENABLE_DOCUMENT_EXTRACTION',
  'AI_FORM_SUMMARISATION',
  'FORM_INSIGHTS_AI',
  'AI_CHAT_ASSISTANT',
  'AI_CONFIG_BUILDER_CHAT',
  'AI_REC_ENGINE_ALTERNATIVES',
  'AI_FORM_GENERATION',
  'AI_REQUEST_NAME_GENERATION',
  'TPRM',
  'ENABLE_ASSESSMENTS',
  'TPRM_CONTINUOUS_MONITORING',
  'CREATE_AGREEMENT',
  'AGREEMENT_ROLLED_OUT',
  'PRICE_BENCHMARKING',
  'SUPPLIER_SUBPROCESSORS',
  'PURCHASE_ORDER_ISSUANCE',
  'OMNEA_ONLY_PURCHASE_ORDERS',
] as const

/**
 * Enriches Mixpanel user properties with feature flag statuses.
 * This merges with existing user properties set in Auth.tsx.
 *
 * Called from FeatureFlagsProvider to ensure feature flags are available.
 */
export const useEnrichUserAnalyticsWithFlags = (userId: string, featureFlags: FeatureFlagsContextValue) => {
  useEffect(() => {
    const featureFlagsToTrack = ANALYTICS_TRACKED_FLAGS.reduce(
      (acc, flag) => ({
        ...acc,
        [flag]: featureFlags[flag] ?? false,
      }),
      {} as Record<string, boolean>
    )

    void analytics.then((analyticsClient) => analyticsClient.identify(userId, { featureFlags: featureFlagsToTrack }))
  }, [userId, featureFlags])
}
